import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'

import type { ClaudeSession } from '@/features/events/conversation/state/types'
import type {
  FormattedAgentActivity,
  FormattedUserActivity,
} from '@/features/events/activity/utils/types'
import {
  SESSION_ADD_FORM_VALIDATION,
  SESSION_OPTIMIZE_BUNDLE_SIZE,
  SESSION_REFACTOR_AUTH_SERVICE,
  SESSION_MIGRATE_DATABASE_SCHEMA,
  SESSION_OPTIMIZE_ENCRYPTION,
  SESSION_DEBUG_PIPELINE_FAILURE,
  SESSION_ACKNOWLEDGED_BASH,
  ACKNOWLEDGED_BASH_DEFAULT_ACTIVITY,
  FIX_RATE_LIMITER_ACTIVITY,
  MIGRATE_DATABASE_ACTIVITY,
  OPTIMIZE_ENCRYPTION_ACTIVITY,
  WAITING_FOR_INPUT_ACTIVITY,
  PROCESSING_REPLY_ACTIVITY,
} from '@/features/home/demo/data'
import { DemoSessionCard } from '@/features/home/demo/components/DemoSessionCard'
import { HOME_CONTENT } from '../defaults'
import { MobileScreenshotFallback } from './MobileScreenshotFallback'

export const EventTilesSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <MobileScreenshotFallback
        headingId="event-tiles-demo-heading"
        title={HOME_CONTENT.tiles.title}
        subtitle={HOME_CONTENT.tiles.description}
        imageSrc={HOME_CONTENT.tiles.image}
        imageAlt={HOME_CONTENT.tiles.imageAlt}
      />
    )
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby="event-tiles-demo-heading"
      sx={sectionContainerSx}
    >
      <Typography component="h2" id="event-tiles-demo-heading" sx={titleSx}>
        {HOME_CONTENT.tiles.title}
      </Typography>
      <Typography sx={subtitleSx}>{HOME_CONTENT.tiles.description}</Typography>

      <Box component="ul" sx={tilesContainerSx} aria-label="Session event cards">
        {TILE_CONFIGS.map((config) => (
          <Box component="li" key={config.ariaLabel} sx={tileItemSx} aria-label={config.ariaLabel}>
            <DemoSessionCard
              session={config.session}
              {...(config.userActivity === undefined ? {} : { userActivity: config.userActivity })}
              {...(config.currentActivity === undefined
                ? {}
                : { currentActivity: config.currentActivity })}
              {...(config.userReplyText === undefined
                ? {}
                : { userReplyText: config.userReplyText })}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Tile config
// ---------------------------------------------------------------------------

interface TileConfig {
  readonly session: ClaudeSession
  readonly ariaLabel: string
  readonly userActivity?: FormattedUserActivity | undefined
  readonly currentActivity?: FormattedAgentActivity | FormattedUserActivity | undefined
  readonly userReplyText?: string | undefined
}

const TILE_CONFIGS: readonly TileConfig[] = [
  {
    session: SESSION_DEBUG_PIPELINE_FAILURE,
    ariaLabel: 'Tool permission request awaiting approval',
  },
  {
    session: SESSION_OPTIMIZE_BUNDLE_SIZE,
    ariaLabel: 'Plan approval event awaiting confirmation',
  },
  {
    session: SESSION_ADD_FORM_VALIDATION,
    ariaLabel: 'Question event requesting user attention',
  },
  {
    session: SESSION_ACKNOWLEDGED_BASH,
    ariaLabel: 'Bash tool use acknowledged with green indicator',
    userActivity: ACKNOWLEDGED_BASH_DEFAULT_ACTIVITY,
    currentActivity: FIX_RATE_LIMITER_ACTIVITY,
  },
  {
    session: SESSION_OPTIMIZE_ENCRYPTION,
    ariaLabel: 'Agent narrative showing encryption optimization',
    currentActivity: OPTIMIZE_ENCRYPTION_ACTIVITY,
  },
  {
    session: SESSION_MIGRATE_DATABASE_SCHEMA,
    ariaLabel: 'File modification event showing database migration',
    currentActivity: MIGRATE_DATABASE_ACTIVITY,
  },
  {
    session: SESSION_REFACTOR_AUTH_SERVICE,
    ariaLabel: 'Agent reply event showing completed refactoring',
    currentActivity: WAITING_FOR_INPUT_ACTIVITY,
  },
  {
    session: SESSION_REFACTOR_AUTH_SERVICE,
    ariaLabel: 'User reply with processing activity',
    userActivity: PROCESSING_REPLY_ACTIVITY,
    currentActivity: PROCESSING_REPLY_ACTIVITY,
    userReplyText:
      'Great, now add unit tests for the TokenValidator covering expired tokens and invalid signatures',
  },
]

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const titleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const subtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
}

const tilesContainerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 960,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  listStyle: 'none',
  margin: 0,
  padding: 0,
}

const tileItemSx: SxProps<Theme> = {
  width: '100%',
}
